import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Exercise,
  ExerciseType,
  Option,
  exerciseTypeOptions,
  MultipleChoiceData,
  FillInBlanksData,
  PronunciationPracticeData,
  WriteTranslationData,
  VideoData,
  ComprehensionQuestionData,
  FormattedTextData,
  GlossaryData,

  isMultipleChoiceData,
  isVideoData,
  isFormattedTextData,
  isWriteTranslationData,
  isPronunciationPracticeData,
  isComprehensionQuestionData,
  isFillInBlanksData,
  isGlossaryData,
} from "../../exercise.interfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { VideoEditor } from "@/pages/Admin/Modules/Edit/exercises/VideoEditor";
import { FormattedTextEditor } from "@/pages/Admin/Modules/Edit/exercises/FormattedTextEditor.tsx";
import { GlossaryEditor } from "@/pages/Admin/Modules/Edit/exercises/GlossaryEditor";

interface ExerciseFormProps {
  type: ExerciseType;
  onChange: (data: Partial<Exercise>) => void;
  initialData?: Exercise;
}

export const ExerciseForm = ({
  type,
  onChange,
  initialData,
}: ExerciseFormProps) => {
  // Estados locais para todos os campos baseados em data tipado
  const [question, setQuestion] = useState<string>(() => {
    if (isMultipleChoiceData(initialData?.data)) {
      return initialData.data.question || "";
    }
    if (isComprehensionQuestionData(initialData?.data)) {
      return initialData.data.question_text || "";
    }
    return "";
  });
  
  const [originalText, setOriginalText] = useState<string>(() => {
    if (isWriteTranslationData(initialData?.data)) {
      return initialData.data.originalText || "";
    }
    return "";
  });
  
  const [translatedText, setTranslatedText] = useState<string>(() => {
    if (isWriteTranslationData(initialData?.data)) {
      return initialData.data.translatedText || "";
    }
    return "";
  });
  
  const [text, setText] = useState<string>(() => {
    if (isFillInBlanksData(initialData?.data)) {
      return initialData.data.text || "";
    }
    return "";
  });
  
  const [sentence, setSentence] = useState<string>(() => {
    if (isPronunciationPracticeData(initialData?.data)) {
      return initialData.data.sentences?.[0] || "";
    }
    return "";
  });
  
  const [title, setTitle] = useState<string>(() => {
    if (isVideoData(initialData?.data)) {
      return initialData.data.title || "";
    }
    return "";
  });
  
  const [videoUrl, setVideoUrl] = useState<string>(() => {
    if (isVideoData(initialData?.data)) {
      return initialData.data.videoUrl || "";
    }
    return "";
  });
  
  const [description, setDescription] = useState<string>("");
  
  const [instructions, setInstructions] = useState<string>(() => {
    if (isPronunciationPracticeData(initialData?.data)) {
      return initialData.data.instructions || "";
    }
    return "";
  });
  
  const [answer, setAnswer] = useState<string>(() => {
    if (isComprehensionQuestionData(initialData?.data)) {
      return initialData.data.answer || "";
    }
    return "";
  });

  // Estados específicos para FormattedText
  const [formattedTextContent, setFormattedTextContent] = useState<string>(() => {
    if (isFormattedTextData(initialData?.data)) {
      return initialData.data.content || "";
    }
    return "";
  });

  // Estados específicos para GlossaryDisplay
  const [glossaryData, setGlossaryData] = useState<GlossaryData | null>(() => {
    if (isGlossaryData(initialData?.data)) {
      return initialData.data;
    }
    return null;
  });

  const [options, setOptions] = useState<Option[]>(() => {
    // Verificar se initialData tem opções usando dados tipados
    if (isMultipleChoiceData(initialData?.data)) {
      const dataOptions = initialData.data.options;
      if (Array.isArray(dataOptions) && dataOptions.length > 0) {
        // Apenas clonar as opções; NÃO forçar uma como correta aqui.
        // Se nenhuma estiver correta, trataremos isso como formato inválido na validação.
        return dataOptions.map((opt) => ({ ...opt }));
      }
    }
    // Estado inicial neutro (nenhuma opção correta por padrão)
    return [{ id: "1", text: "", isCorrect: false }];
  });

  // Atualiza todos os campos quando initialData muda usando dados tipados
  useEffect(() => {
    if (initialData?.data) {
      // Processar GlossaryData PRIMEIRO antes de resetar outros campos
      // Isso garante que os dados sejam preservados durante o reset
      if (isGlossaryData(initialData.data)) {
        setGlossaryData(initialData.data);
      } else {
        setGlossaryData(null);
      }
      
      // Reset all other fields
      setQuestion("");
      setOriginalText("");
      setTranslatedText("");
      setText("");
      setSentence("");
      setTitle("");
      setVideoUrl("");
      setDescription("");
      setInstructions("");
      setAnswer("");
      setOptions([{ id: "1", text: "", isCorrect: true }]);

      // Update fields based on data type
      if (isMultipleChoiceData(initialData.data)) {
        const incomingQuestion = initialData.data.question || "";
        setQuestion(incomingQuestion);
        const dataOptions = initialData.data.options;

        if (Array.isArray(dataOptions) && dataOptions.length > 0) {
          // Normalizar propriedades lidando com PascalCase e camelCase
          const normalizedOptions = dataOptions.map((opt, index) => {
            const text = opt?.text || opt?.Text || '';
            const isCorrect = opt?.isCorrect === true || opt?.IsCorrect === true;
            const order = opt?.order !== undefined ? opt.order :
                         opt?.Order !== undefined ? opt.Order : index;

            return {
              id: (index + 1).toString(),
              text,
              isCorrect,
              order
            };
          });

          const hasCorrectOption = normalizedOptions.some((opt) => opt.isCorrect);

          if (!hasCorrectOption) {
            console.error('⚠️ Dados de múltipla escolha inválidos: nenhuma opção marcada como correta.');
          }

          const finalOptions = normalizedOptions;

          // Atualiza o estado local para refletir as opções normalizadas
          setOptions(finalOptions);

          // IMPORTANTE: sincronizar imediatamente com o pai para que a validação
          // enxergue pelo menos uma opção correta mesmo que o usuário não edite nada
          const dataToSend = buildTypedData({
            question: incomingQuestion,
            options: finalOptions
          });

          // Only trigger onChange if the data effectively changed (normalization)
          // This prevents infinite loops where useEffect triggers update -> parent re-renders -> useEffect triggers update
          const shouldUpdate = JSON.stringify(dataToSend.data) !== JSON.stringify(initialData.data);

          if (shouldUpdate) {
            onChange(dataToSend);
          }
        }
      } else if (isComprehensionQuestionData(initialData.data)) {
        setQuestion(initialData.data.question_text || "");
        setAnswer(initialData.data.answer || "");
      } else if (isWriteTranslationData(initialData.data)) {
        setOriginalText(initialData.data.originalText || "");
        setTranslatedText(initialData.data.translatedText || "");
      } else if (isFillInBlanksData(initialData.data)) {
        setText(initialData.data.text || "");
      } else if (isPronunciationPracticeData(initialData.data)) {
        setSentence(initialData.data.sentences?.[0] || "");
        setInstructions(initialData.data.instructions || "");
      } else if (isVideoData(initialData.data)) {
        setTitle(initialData.data.title || "");
        setVideoUrl(initialData.data.videoUrl || "");
      } else if (isFormattedTextData(initialData.data)) {
        setFormattedTextContent(initialData.data.content || "");
      }
      // GlossaryData já foi processado no início do useEffect, não precisa processar novamente
    }
  }, [initialData]);

  // Helper para construir dados tipados baseado no tipo
  const buildTypedData = (updates: any = {}) => {
    switch (type) {
      case ExerciseType.MultipleChoice: {
        const finalOptions = updates.options ?? options;
        
        return {
          data: {
            question: updates.question ?? question,
            options: finalOptions,
            multipleCorrectAnswers: finalOptions.filter((opt: Option) => opt.isCorrect).length > 1,
          } as MultipleChoiceData
        };
      }
      
      case ExerciseType.WriteTheTranslation:
        return {
          data: {
            originalText: updates.originalText ?? originalText,
            translatedText: updates.translatedText ?? translatedText,
          } as WriteTranslationData
        };
      
      case ExerciseType.FillInBlanks:
      case ExerciseType.FillInTheBlanks:
        return {
          data: {
            text: updates.text ?? text,
            blanks: [], // TODO: implement blanks extraction from text
          } as FillInBlanksData
        };
      
      case ExerciseType.PronunciationPractice:
        return {
          data: {
            sentences: [updates.sentence ?? sentence],
            instructions: updates.instructions ?? instructions,
          } as PronunciationPracticeData
        };
      
        case ExerciseType.Video:
        case ExerciseType.WatchVideo:
        case ExerciseType.WatchLargeVideo:
        return {
          data: {
            title: updates.title ?? title,
            videoUrl: updates.videoUrl ?? videoUrl,
          } as VideoData
        };
      
      case ExerciseType.ComprehensionQuestion:
        return {
          data: {
            question_text: updates.question ?? question,
            answer: updates.answer ?? answer,
          } as ComprehensionQuestionData
        };
      
      case ExerciseType.FormattedText:
        return {
          data: {
            content: updates.formattedTextContent ?? formattedTextContent,
          } as FormattedTextData
        };
      
      case ExerciseType.GlossaryDisplay:
        return {
          data: updates.glossaryData ?? glossaryData ?? {
            title: "",
            topics: []
          } as GlossaryData
        };
      
      default:
        return { data: updates };
    }
  };

  // Handlers atualizados para usar dados tipados
  const handleQuestionChange = (value: string) => {
    setQuestion(value);
    onChange(buildTypedData({ question: value }));
  };

  const handleOriginalTextChange = (value: string) => {
    setOriginalText(value);
    onChange(buildTypedData({ originalText: value }));
  };

  const handleTranslatedTextChange = (value: string) => {
    setTranslatedText(value);
    onChange(buildTypedData({ translatedText: value }));
  };

  const handleOptionChange = (id: string, text: string) => {
    const newOptions = options.map((opt) =>
      opt.id === id ? { ...opt, text } : { ...opt }
    );
    setOptions(newOptions);
    onChange(buildTypedData({ options: newOptions }));
  };

  const addOption = () => {
    // Gerar ID único baseado no maior ID existente + 1
    const maxId = options.length > 0 
      ? Math.max(...options.map(opt => parseInt(opt.id) || 0))
      : 0;
    
    const newOption = {
      id: (maxId + 1).toString(),
      text: "",
      isCorrect: false,
    };
    const newOptions = [...options, newOption];
    setOptions(newOptions);
    onChange(buildTypedData({ options: newOptions }));
  };

  const removeOption = (id: string) => {
    const newOptions = options.filter((opt) => opt.id !== id);
    setOptions(newOptions);
    onChange(buildTypedData({ options: newOptions }));
  };

  const setCorrectAnswer = (id: string) => {
    const newOptions = options.map((opt) => {
      if (opt.id === id) {
        return { ...opt, isCorrect: !opt.isCorrect };
      }
      return { ...opt };
    });

    setOptions(newOptions);
    const dataToSend = buildTypedData({ options: newOptions });

    onChange(dataToSend);
  };

  // Handlers adicionais para todos os campos
  const handleTextChange = (value: string) => {
    setText(value);
    onChange(buildTypedData({ text: value }));
  };

  const handleSentenceChange = (value: string) => {
    setSentence(value);
    onChange(buildTypedData({ sentence: value }));
  };

  // Handlers para vídeo são gerenciados pelo VideoEditor component

  const handleAnswerChange = (value: string) => {
    setAnswer(value);
    onChange(buildTypedData({ answer: value }));
  };



  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormattedTextContentChange = (value: string) => {
    setFormattedTextContent(value);
    onChange(buildTypedData({ formattedTextContent: value }));
  };

  const renderMultipleChoiceFields = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="question">Pergunta</Label>
        <Textarea
          id="question"
          value={question}
          onChange={(e) => handleQuestionChange(e.target.value)}
          placeholder="Digite a pergunta do exercício"
        />
      </div>

      <div className="space-y-2">
        <Label>Opções (múltiplas respostas corretas permitidas)</Label>
        {options.map((option, index) => (
          <div key={option.id} className="flex gap-2 items-center">
            <Input
              value={option.text}
              onChange={(e) => handleOptionChange(option.id, e.target.value)}
              placeholder={`Opção ${index + 1}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeOption(option.id)}
            >
              <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant={option.isCorrect ? "default" : "outline"}
              onClick={() => setCorrectAnswer(option.id)}
              size="sm"
            >
              {option.isCorrect ? "✓ Correta" : "Marcar como Correta"}
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addOption}
          className="flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4 mr-2" />
          Adicionar Opção
        </Button>
      </div>
    </div>
  );

  const renderWriteTranslationFields = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="original">Texto Original</Label>
        <Textarea
          id="original"
          value={originalText}
          onChange={(e) => handleOriginalTextChange(e.target.value)}
          placeholder="Digite o texto original"
        />
      </div>
      <div>
        <Label htmlFor="translation">Tradução</Label>
        <Textarea
          id="translation"
          value={translatedText}
          onChange={(e) => handleTranslatedTextChange(e.target.value)}
          placeholder="Digite a tradução esperada"
        />
      </div>
    </div>
  );

  const renderVideoFields = () => {
    // Adaptador para converter entre os formatos antigo e novo
    const videoExercise: any = {
      type: ExerciseType.Video,
      data: {
        videoUrl: videoUrl,
        video: "", // Valor padrão
        title: title,
        description: description,
        instructions: instructions,
      },
    };

    // Função para adaptar as mudanças do novo formato para o antigo
    const handleVideoEditorChange = (newValue: any) => {
      // Extrair os dados relevantes do objeto retornado pelo editor
      const { data } = newValue;

      setVideoUrl(data.videoUrl);
      setTitle(data.title);
      setDescription(data.description);
      setInstructions(data.instructions);

      // Usar o novo formato tipado com data
      onChange(buildTypedData({
        title: data.title,
        videoUrl: data.videoUrl,
        description: data.description,
        instructions: data.instructions,
      }));
    };

    return (
      <VideoEditor value={videoExercise} onChange={handleVideoEditorChange} />
    );
  };

  const renderFillInBlanksFields = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="text">Texto para Preenchimento</Label>
        <Textarea
          id="text"
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder="Digite o texto com lacunas usando ___ para indicar onde o aluno deve preencher"
        />
      </div>
      <div className="space-y-2">
        <Label>Respostas Corretas</Label>
        {options.map((option) => (
          <div key={option.id} className="flex gap-2">
            <Input
              value={option.text}
              onChange={(e) => handleOptionChange(option.id, e.target.value)}
              placeholder={`Resposta ${option.id}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeOption(option.id)}
            >
              <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addOption}
          className="flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4 mr-2" />
          Adicionar Resposta
        </Button>
      </div>
    </div>
  );

  const renderPronunciationPracticeFields = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="sentence">Frase para Falar</Label>
        <Textarea
          id="sentence"
          value={sentence}
          onChange={(e) => handleSentenceChange(e.target.value)}
          placeholder="Digite a frase que o aluno deve falar"
        />
      </div>
    </div>
  );


  const renderFormattedTextFields = () => {
    // Adaptador para converter entre os formatos antigo e novo
    const formattedTextExercise: any = {
      type: ExerciseType.FormattedText,
      data: {
        content: formattedTextContent || (isFormattedTextData(initialData?.data) ? initialData.data.content : ""),
      },
    };

    // Função para adaptar as mudanças do editor para o formato tipado
    const handleFormattedTextEditorChange = (data: any) => {
      setFormattedTextContent(data.content || "");
      const dataToSend = buildTypedData({
        formattedTextContent: data.content,
      });
      onChange(dataToSend);
    };

    return (
      <div className="space-y-4">
        <FormattedTextEditor 
          initialData={isFormattedTextData(initialData?.data) ? initialData.data : formattedTextExercise.data} 
          onChange={handleFormattedTextEditorChange} 
        />
      </div>
    );
  };

  const renderGlossaryFields = () => {
    // Usar glossaryData do estado, mas fazer fallback para initialData.data se glossaryData for null
    // Isso garante que os dados sejam passados mesmo antes do useEffect atualizar o estado
    const currentGlossaryData: GlossaryData = glossaryData || 
      (isGlossaryData(initialData?.data) ? initialData.data : {
        title: "",
        topics: []
      } as GlossaryData);

    // Construir objeto Exercise completo para o GlossaryEditor
    // Não usar useMemo aqui pois hooks não podem ser chamados condicionalmente
    const glossaryExercise: Exercise = {
      type: ExerciseType.GlossaryDisplay,
      id: initialData?.id,
      data: currentGlossaryData
    };

      // Adapter para converter onChange do GlossaryEditor para o padrão do ExerciseForm
      const handleGlossaryEditorChange = (newValue: any) => {
        // O GlossaryEditor retorna um ComponentExercise completo via EditorProps
        // Precisamos extrair apenas os dados e atualizar o estado local
        if (newValue?.data && isGlossaryData(newValue.data)) {
          setGlossaryData(newValue.data);
          // Chamar onChange do ExerciseForm com Partial<Exercise>
          // Sincronizar o título do exercício com o título do glossário
          onChange({
            type: ExerciseType.GlossaryDisplay,
            title: newValue.data.title,
            data: newValue.data
          });
        }
      };

    return (
      <div className="space-y-4">
        <GlossaryEditor
          value={glossaryExercise as any} // Cast necessário devido à diferença de tipos
          onChange={handleGlossaryEditorChange}
        />
      </div>
    );
  };

  const renderComprehensionQuestionFields = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="question">Pergunta</Label>
        <Textarea
          id="question"
          value={question}
          onChange={(e) => handleQuestionChange(e.target.value)}
          placeholder="Digite a pergunta de compreensão"
        />
      </div>
      <div>
        <Label htmlFor="answer">Resposta</Label>
        <Textarea
          id="answer"
          value={answer}
          onChange={(e) => handleAnswerChange(e.target.value)}
          placeholder="Digite a resposta correta"
        />
      </div>
    </div>
  );

  const renderFields = () => {
    switch (type) {
      case ExerciseType.MultipleChoice:
        return renderMultipleChoiceFields();
      case ExerciseType.WriteTranslation:
        return renderWriteTranslationFields();
      case ExerciseType.Video:
      case ExerciseType.WatchVideo:
      case ExerciseType.WatchLargeVideo:
        return renderVideoFields();
      case ExerciseType.FillInBlanks:
        return renderFillInBlanksFields();
      case ExerciseType.PronunciationPractice:
        return renderPronunciationPracticeFields();
      case ExerciseType.ComprehensionQuestion:
        return renderComprehensionQuestionFields();
      case ExerciseType.FormattedText:
        return renderFormattedTextFields();
      case ExerciseType.GlossaryDisplay:
        return renderGlossaryFields();
        /* Antigo editor removido
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="formatted-text-title">Título do Texto</Label>
              <Input
                id="formatted-text-title"
                type="text"
                placeholder="Digite o título do texto"
                value={formattedTextTitle}
                onChange={(e) => handleFormattedTextTitleChange(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="formatted-text-content">
                Conteúdo (suporta Markdown)
              </Label>
              <Textarea
                id="formatted-text-content"
                placeholder="Digite o conteúdo do texto (suporta markdown)"
                rows={8}
                value={formattedTextContent}
                onChange={(e) => handleFormattedTextContentChange(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Você pode usar markdown para formatação: **negrito**, *itálico*,
                [link](url), etc.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="formatted-text-bg-color">Cor de Fundo</Label>
              <div className="flex items-center gap-2">
                <input
                  id="formatted-text-bg-color"
                  type="color"
                  title="Selecionar cor de fundo"
                  value={formattedTextBgColor}
                  onChange={(e) => handleFormattedTextBgColorChange(e.target.value)}
                  className="w-16 h-10 p-1 border rounded"
                />
                <Input
                  type="text"
                  value={formattedTextBgColor}
                  title="Valor da cor hexadecimal"
                  onChange={(e) => handleFormattedTextBgColorChange(e.target.value)}
                  placeholder="#f9f9f9"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        );
        */
      default:
        console.warn(`Unsupported exercise type: ${type}`);
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="type">Tipo de Exercício</Label>
        <Select
          value={type}
          disabled={!!initialData?.id}
          onValueChange={(value) => {
            const newType = value as ExerciseType;
            // Limpar campos específicos do tipo anterior
            const cleanedData: Partial<Exercise> = {
              type: newType,
              // Manter apenas campos comuns
              id: initialData?.id,
            };
            onChange(cleanedData);
          }}
        >
          <SelectTrigger id="type">
            <SelectValue placeholder="Selecione o tipo de exercício" />
          </SelectTrigger>
          <SelectContent>
            {exerciseTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {renderFields()}
    </div>
  );
};
